import { createContext, useContext, useSyncExternalStore } from "react";
import type { ApiClient } from "../api/client.js";
import type { SessionStore, StoredSession } from "../auth/session.js";
import type { KnownRecords } from "../lib/known-records.js";

/**
 * What every screen needs: a client, the session it is acting under, and the
 * ids this browser knows about.
 *
 * Passed as one context rather than three so a test can assemble the whole
 * environment in a single object — which is what makes it cheap to drive these
 * components against the real API rather than against stubs.
 */
export interface Portal {
  readonly client: ApiClient;
  readonly session: SessionStore;
  readonly known: KnownRecords;
}

const PortalContext = createContext<Portal | null>(null);

export const PortalProvider = PortalContext.Provider;

export function usePortal(): Portal {
  const portal = useContext(PortalContext);
  if (!portal) {
    throw new Error(
      "usePortal was called outside PortalProvider. Wrap the tree in <PortalProvider>.",
    );
  }
  return portal;
}

/** Re-renders when the user signs in or out. */
export function useSession(): StoredSession | null {
  const { session } = usePortal();
  return useSyncExternalStore(
    session.subscribe,
    session.getSnapshot,
    session.getSnapshot,
  );
}
